// src/ui/screens/FullProfileImageScreen.jsx

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

const COLORS = {
  bg: '#08090B',
  card: '#23262C',
};

export const FullProfileImageScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { imageUrl, name } = location.state || {};

  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // If no imageUrl, go back
  if (!imageUrl) {
    navigate(-1);
    return null;
  }

  return (
    <div className="min-h-screen bg-filatom-bg flex items-center justify-center">
      <div className="relative w-full h-full min-h-screen flex flex-col items-center justify-center">
        {/* Full Image with Zoom/Pan */}
        <div className="w-full h-full flex-1 flex items-center justify-center p-4">
          <TransformWrapper
            centerOnInit
            minScale={0.5}
            maxScale={4}
            initialScale={1}
            wheel={{ step: 0.1 }}
          >
            {({ zoomIn, zoomOut, resetTransform }) => (
              <div className="relative w-full h-full flex items-center justify-center">
                <TransformComponent>
                  {imageError ? (
                    <div className="w-[200px] h-[200px] rounded-full bg-filatom-cardLight flex items-center justify-center">
                      <svg className="w-20 h-20 text-white/30" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                      </svg>
                    </div>
                  ) : (
                    <img
                      src={imageUrl}
                      alt={name || 'Profile'}
                      className="max-h-[85vh] max-w-[85vw] object-contain rounded-lg"
                      style={{
                        transition: 'opacity 0.3s ease',
                        opacity: imageLoaded ? 1 : 0,
                      }}
                      onLoad={() => setImageLoaded(true)}
                      onError={() => setImageError(true)}
                    />
                  )}
                </TransformComponent>

                {/* Zoom Controls - positioned inside the transform wrapper */}
                <div className="absolute bottom-28 right-4 flex flex-col gap-1.5 z-10">
                  <button
                    onClick={() => zoomIn()}
                    className="w-10 h-10 rounded-full bg-filatom-card/80 backdrop-blur-sm border border-white/10 flex items-center justify-center hover:bg-filatom-card transition-colors"
                  >
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </button>
                  <button
                    onClick={() => zoomOut()}
                    className="w-10 h-10 rounded-full bg-filatom-card/80 backdrop-blur-sm border border-white/10 flex items-center justify-center hover:bg-filatom-card transition-colors"
                  >
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                    </svg>
                  </button>
                  <button
                    onClick={() => resetTransform()}
                    className="w-10 h-10 rounded-full bg-filatom-card/80 backdrop-blur-sm border border-white/10 flex items-center justify-center hover:bg-filatom-card transition-colors"
                  >
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </TransformWrapper>
        </div>

        {/* Name Overlay at Bottom */}
        {name && name.length > 0 && (
          <div className="absolute bottom-10 left-0 right-0 flex justify-center px-4 pointer-events-none">
            <div className="max-w-md w-full px-6 py-3 rounded-2xl bg-black/50 backdrop-blur-sm border border-white/10">
              <p className="text-white text-lg font-semibold text-center truncate">
                {name}
              </p>
            </div>
          </div>
        )}

        {/* Close Button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 w-11 h-11 rounded-full flex items-center justify-center bg-filatom-card/50 backdrop-blur-xl border border-filatom-card/30 hover:bg-filatom-card/70 transition-colors z-10"
        >
          <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Pinch to Zoom Hint */}
        <div className="absolute bottom-24 left-0 right-0 flex justify-center pointer-events-none">
          <div className="px-4 py-1.5 rounded-full bg-white/5 backdrop-blur-sm">
            <div className="flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5 text-white/35" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
              </svg>
              <span className="text-white/35 text-xs font-light">Pinch to zoom</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FullProfileImageScreen;